import { Button } from "@/components/flexkit/Button";
import { Alert } from "@/components/podkit/alert/Alert";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { useMembership } from "@/hooks/use-membership";
import { TrackLocations, type TrackLocation } from "@/hooks/use-segment";
import { getRepoUrlFromInitializer, useEnvironment } from "@/queries/environment-queries";
import { useCreateProjectFromEnvironment } from "@/queries/project-queries";
import { useGetEnvironmentClass } from "@/queries/runner-configuration-queries";
import { useIsUserAuthenticatedWithRunner, type AuthenticatedWithRunnerResponse } from "@/queries/runner-queries";
import { SCMAuthenticationModal } from "@/routes/environments/create/SCMAuthentication";
import { formatError } from "@/utils/errors";
import { Code, ConnectError } from "@connectrpc/connect";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useId, useMemo, useState, type FC, type FormEvent } from "react";

export const CreateProjectFromEnvironmentModal: FC<{
    environmentId: string;
    onClose: () => void;
    onSuccess?: () => void;
}> = ({ environmentId, onClose, onSuccess }) => {
    const { toast } = useToast();

    const [projectName, setProjectName] = useState<string>();
    const inputNameID = useId();

    const [isFailedWithAuthenticationRequired, setIsFailedWithAuthenticationRequired] = useState(false);

    const [showScmAuthModal, setShowScmAuthModal] = useState<SCMAuthenticationModalProps | undefined>();

    const createProjectFromEnvironment = useCreateProjectFromEnvironment();
    const membership = useMembership();
    const isAdmin = membership.membership?.userRole === OrganizationRole.ADMIN;

    const { data: environment } = useEnvironment(environmentId);
    const { data: clazz } = useGetEnvironmentClass(environment?.spec?.machine?.class);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const repoURL = useMemo(() => {
        return getRepoUrlFromInitializer(environment?.spec?.content?.initializer);
    }, [environment]);

    const { data: authCheckResponse } = useIsUserAuthenticatedWithRunner(environment?.metadata?.runnerId, repoURL, {
        refetchUntilAuthenticated: true,
        sessionId: environment?.spec?.machine?.session,

        // Auth check should be polled only when content is in a failed state.
        enabled: isFailedWithAuthenticationRequired,
    });

    const isAuthenticationRequired =
        isFailedWithAuthenticationRequired && authCheckResponse?.type == "AuthenticationRequired";

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!projectName) {
                return;
            }

            createProjectFromEnvironment.mutate(
                {
                    projectName,
                    environmentId,
                },
                {
                    onSuccess: () => {
                        onClose();
                        onSuccess?.();
                    },
                    onError: (e) => {
                        if (e instanceof ConnectError && e.code === Code.FailedPrecondition) {
                            setIsFailedWithAuthenticationRequired(true);
                        }
                        toast({
                            title: "Failed to create project from environment",
                            description: formatError(e),
                        });
                    },
                },
            );
        },
        [createProjectFromEnvironment, toast, onClose, onSuccess, projectName, environmentId],
    );

    const reAuthenticate = useMemo(() => {
        if (!isAuthenticationRequired) {
            return;
        }
        return (
            <Button
                size="sm"
                variant="primary"
                onClick={() => {
                    if (!authCheckResponse || !clazz) {
                        return;
                    }
                    setShowScmAuthModal({
                        repoURL: repoURL || "",
                        runnerId: clazz.runnerId,
                        authResponse: authCheckResponse,
                        onClose: () => setShowScmAuthModal(undefined),
                        onAuthSuccess: () => {
                            setShowScmAuthModal(undefined);
                        },
                        "data-track-location": TrackLocations.EnvironmentSCMAuthenticationModal,
                    });
                }}
                data-track-label="true"
                data-tracking-id="reauthenticate-scm-create-project-from-environment-modal"
            >
                Re-Authenticate
            </Button>
        );
    }, [authCheckResponse, clazz, isAuthenticationRequired, repoURL]);

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-track-location={TrackLocations.EnvironmentOnboardingCreateProjectFromEnvironmentModal}
            >
                <DialogHeader>
                    <DialogTitle>Create project from environment</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <DialogBody className="flex max-w-full flex-col gap-2 overflow-hidden">
                        <InputField label="Project name" id={inputNameID}>
                            <Input
                                id={inputNameID}
                                type="text"
                                value={projectName || ""}
                                placeholder="Ex: My Project"
                                onChange={(e) => setProjectName(e.target.value.trimStart().replace(/\s+/g, " "))}
                            />
                        </InputField>
                        {isAdmin && (
                            <div className="px-1 text-sm text-content-secondary">
                                Your project will be shared with everyone in your organization. You can change that
                                anytime in the project settings.
                            </div>
                        )}
                        {isFailedWithAuthenticationRequired && (
                            <>
                                <Alert className="mt-4 flex flex-row gap-2 bg-surface-02">
                                    <Text>
                                        You need to authenticate with the environment before creating a project.
                                    </Text>
                                    <div>{reAuthenticate}</div>
                                </Alert>
                                {showScmAuthModal && (
                                    <AuthenticationModal
                                        {...showScmAuthModal}
                                        onClose={() => setShowScmAuthModal(undefined)}
                                        onAuthSuccess={() => {
                                            setShowScmAuthModal(undefined);
                                            setIsFailedWithAuthenticationRequired(false);
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </DialogBody>

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="secondary">
                                Close
                            </Button>
                        </DialogClose>
                        <Button
                            type="submit"
                            variant="primary"
                            loading={createProjectFromEnvironment.isPending}
                            disabled={!projectName || isFailedWithAuthenticationRequired}
                            data-tracking-id="create-project-create-project-from-environment-modal"
                        >
                            Create
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export type SCMAuthenticationModalProps = {
    repoURL: string;
    authResponse: AuthenticatedWithRunnerResponse;
    runnerId: string;
    onClose: () => void;
    onAuthSuccess: () => void;
    "data-track-location": TrackLocation;
};

export const AuthenticationModal: FC<SCMAuthenticationModalProps> = (p) => {
    const patAuth = useMemo(() => {
        return p.authResponse.type === "AuthenticationRequired" ? p.authResponse.patAuth : undefined;
    }, [p]);
    const authUrl = useMemo(() => {
        return p.authResponse.type === "AuthenticationRequired" ? p.authResponse.url : "";
    }, [p]);
    const scmId = useMemo(() => {
        return p.authResponse.type === "AuthenticationRequired" ? p.authResponse.scmId : "";
    }, [p]);
    const scmName = useMemo(() => {
        return p?.authResponse?.scmName || "";
    }, [p]);

    return (
        <div>
            <SCMAuthenticationModal
                repoURL={p.repoURL}
                authenticationUrl={authUrl}
                patAuth={patAuth}
                scmId={scmId}
                scmName={scmName}
                onClose={p.onClose}
                onClickContinue={p.onAuthSuccess}
                runnerId={p.runnerId}
                data-track-location={p["data-track-location"]}
            />
        </div>
    );
};
